import { useEffect, useState } from "react";
import { API, getOrCreateGuestId } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { DownloadSimple, FileImage, WarningCircle } from "@phosphor-icons/react";

export default function ReceiptViewer({ fileId, trigger }) {
  const [open, setOpen] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [contentType, setContentType] = useState("");
  const [filename, setFilename] = useState("receipt");
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    let createdUrl = null;
    setError(false);
    setBlobUrl(null);
    (async () => {
      try {
        const r = await fetch(`${API}/files/${fileId}/download`, {
          credentials: "include",
          headers: { "X-Guest-Id": getOrCreateGuestId() },
        });
        if (!r.ok) throw new Error(`status ${r.status}`);
        const blob = await r.blob();
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setBlobUrl(createdUrl);
        const ct = blob.type || r.headers.get("Content-Type") || "";
        setContentType(ct);
        // Derive a filename from content-disposition if present
        const cd = r.headers.get("Content-Disposition") || "";
        const m = cd.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)/i);
        if (m) setFilename(decodeURIComponent(m[1]));
        else {
          const ext = ct.split("/")[1] || "bin";
          setFilename(`receipt.${ext}`);
        }
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [open, fileId]);

  const isImage = contentType.startsWith("image/");
  const isPDF = contentType.includes("pdf");

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-3xl max-h-[92vh] rounded-2xl p-0 overflow-hidden" data-testid={`receipt-viewer-${fileId}`}>
        <DialogHeader className="px-6 pt-6 pb-3 flex flex-row items-center justify-between gap-3">
          <DialogTitle className="flex items-center gap-2">
            <FileImage weight="duotone" size={20} className="text-[#2C4C3B]" /> Receipt
          </DialogTitle>
          {blobUrl && (
            <a
              href={blobUrl}
              download={filename}
              data-testid="receipt-download"
              className="inline-flex items-center gap-1.5 text-sm rounded-full border border-[#E3E1D9] px-3 py-1.5 hover:bg-[#EBE9E0]/60"
            >
              <DownloadSimple size={16} weight="bold" /> Save
            </a>
          )}
        </DialogHeader>

        <div className="bg-[#EBE9E0]/40 border-t border-[#E3E1D9] min-h-[300px] max-h-[75vh] overflow-auto flex items-center justify-center">
          {error ? (
            <div className="text-center p-10">
              <WarningCircle size={32} weight="duotone" className="text-[#C17767] mx-auto" />
              <p className="mt-3 text-sm text-[#1E2420]">We couldn't load that receipt.</p>
              <p className="text-xs text-[#5C6660] mt-1">It may have been removed or your session expired.</p>
            </div>
          ) : !blobUrl ? (
            <div className="text-center p-10">
              <div className="w-10 h-10 mx-auto rounded-full border-2 border-[#2C4C3B] border-t-transparent animate-spin" />
              <p className="mt-3 text-sm text-[#5C6660]">Loading receipt…</p>
            </div>
          ) : isImage ? (
            <img
              src={blobUrl}
              alt="Receipt"
              className="max-w-full max-h-[75vh] object-contain"
              data-testid="receipt-image"
            />
          ) : isPDF ? (
            <iframe
              src={blobUrl}
              title="Receipt"
              className="w-full h-[75vh] bg-white"
              data-testid="receipt-pdf"
            />
          ) : (
            <div className="text-center p-10">
              <FileImage size={32} weight="duotone" className="text-[#2C4C3B] mx-auto" />
              <p className="mt-3 text-sm text-[#1E2420]">Preview not supported for this file type.</p>
              <a
                href={blobUrl}
                download={filename}
                className="inline-flex items-center gap-1.5 text-sm rounded-full bg-[#2C4C3B] text-white px-4 py-2 mt-4 hover:bg-[#3A634D]"
              >
                <DownloadSimple size={16} weight="bold" /> Download to view
              </a>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
